/**
 * ST-08 — Copy the reply to the clipboard (AIVA-6798).
 *
 * Copies the RAW reply text, exactly as the user read it — not the
 * PowerPoint-sanitised variant the slide buttons place ({@link toSlideText}
 * strips Markdown and bullet markers), so nothing is lost when it is pasted
 * somewhere else (spec ST-08).
 *
 * Plain text only: unlike Word's copy there is no document to match, and the
 * reply is plain text by the PowerPoint response contract.
 *
 * Toasts are not raised here. Success is confirmed by the action bar's
 * PPT_WRITE_SUCCESS_TOASTS table and a failure by powerPointFailureToast, so
 * every PowerPoint button reports through the same place.
 */

import { execCommandCopyFallback } from '../../../../integrations/tools/clipboard';

/** Thrown when neither the async Clipboard API nor the execCommand fallback worked. */
export const COPY_FAILED_ERROR = 'Clipboard unavailable on this PowerPoint host';

export interface CopyResponseResult {
  readonly copied: true;
}

/**
 * Put `text` on the clipboard.
 *
 * Some hosts load the add-in without `clipboard-write` in their
 * Permissions-Policy, so a missing or rejecting async API falls back to the
 * shared execCommand copy before giving up with {@link COPY_FAILED_ERROR}.
 */
export async function copyResponseText(text: string): Promise<CopyResponseResult> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return { copied: true };
    } catch {
      // Permissions policy / NotAllowedError — fall through to execCommand.
    }
  }
  if (execCommandCopyFallback(text)) return { copied: true };
  throw new Error(COPY_FAILED_ERROR);
}
